import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Box,
  CircularProgress,
  Fab,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import { ApolloError } from "@apollo/client";
import _, { merge, property } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateBranch } from "../../../../../actions/organisation/actions";
import { updateUserCurrentBranch } from "../../../../../actions/user/actions";
import { GET_ORGANISATIONS } from "../../../../../graphql/organisation/getOrganisationQuery";
import { UPDATE_RENTAL_AGREEMENT_SETTINGS } from "../../../../../graphql/organisation/updateRentalAgreementSettingsMutation";
import { IBranch, IOrganisation, IRentalAgreementSettings } from "../../../../../reducers/organisation/types";
import { IAppState } from "../../../../../store";
import { getLocalizedBookingSyntex, getLocalizedOrganisationSyntex } from "../../../../../utils/localized.syntex";
import { useSnackBar } from "../../../../common/SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../../../../common/SnackbarWrapper/SnackbarWrapper";
import { formatGraphQLErrorMessage } from "../../../../common/utils";
import { NuvvenDivider } from "../../../ReservationManagement/Summary/Components/Divider";
import { CustomerType } from "../../../ReservationManagement/utils";
import RAPreviewFormatIndividualCustomer from "./RAPreviewFormatIndividualCustomer";
import RowItemsSelector from "./RowItemsSelector";
import { ADDON_DETAILS_LIST, INSURANCE_DETAILS_LIST, BOOKING_DETAILS_LIST, BOOKING_EXTENSION_DETAILS_LIST, CUSTOMER_INFO_LIST, DRIVER_DETAILS_LIST, OLD_VEHICLE_DETAILS, PAYMENT_INFO_LIST, SettingsMap, VEHICLE_DETAILS_LIST, RentalAgreementSettingsMap, PROTECTION_PLAN_DETAILS_LIST } from "./utils";
import { UserRoles } from "../../../../hoc/Authorization";
import { replaceNullsWithKeys } from "../SubscriptionAgreementSettings/utils";

interface IProps {
  customerType: 'individual' | 'business';
}

const CustomerBasedSettings: React.FC<IProps> = (props) => {
  const snackbar = useSnackBar();
  const dispatch = useDispatch();
  const [organisations, setOrganisations] = useState<IOrganisation[]>([]);
  const [branches, setBranches] = useState<IBranch[]>([]);
  const [selectedOrganisationId, setSelectedOrganisationId] = useState<string>("");
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [selectedBranch, setSelectedBranch] = useState<IBranch | any>();
  const [openPreview, setOpenPreview] = useState<boolean>(false)
  const [selectedOrgainisation, setSelectedOrganisation] = useState<IOrganisation>()
  const userState = useSelector((state: IAppState) => state.userReducer);
  const { country } = userState.currentOrganisation.address;

  const [getOrganisations, { loading: organisationLoading, data: organisationsData }] = useLazyQuery(GET_ORGANISATIONS, {
    fetchPolicy: "network-only",
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    }
  });

  useEffect(() => {
    if (userState) {
      getOrganisations();
    }
  }, [userState])

  useEffect(() => {
    if (organisationsData && organisationsData.organisations) {
      setOrganisations(organisationsData.organisations)
    }
  }, [organisationsData])

  const updateSelected = (selected: string[], itemName: string, rentalAgreementSettingsMap?: any) => {
    const item = SettingsMap[itemName];
    const rentalAgreementSettings = { ...selectedBranch?.rentalAgreementSettings };
    let rASettingsMap = { ...selectedBranch.rentalAgreementSettingsMap };
    if (rentalAgreementSettingsMap) {
      if (props.customerType === CustomerType.INDIVIDUAL) {
        rASettingsMap = {
          individual: _.merge(RentalAgreementSettingsMap(selectedBranch.address.country), rASettingsMap.individual, rentalAgreementSettingsMap),
          business: {
            ...RentalAgreementSettingsMap(selectedBranch.address.country),
            ...rASettingsMap.business
          }
        }
      } else {
        rASettingsMap = {
          business: _.merge(RentalAgreementSettingsMap(selectedBranch.address.country), rASettingsMap.business, rentalAgreementSettingsMap),
          individual: {
            ...RentalAgreementSettingsMap(selectedBranch.address.country),
            ...rASettingsMap.individual
          }
        }
      }
    }
    if (rentalAgreementSettings) {
      if (props.customerType === CustomerType.INDIVIDUAL) {
        rentalAgreementSettings.individual = {
          ...rentalAgreementSettings.individual,
          [item]: selected
        }
      } else {
        rentalAgreementSettings.business = {
          ...rentalAgreementSettings.business,
          [item]: selected
        }
      }
    }
    setSelectedBranch({
      ...selectedBranch,
      rentalAgreementSettings,
      rentalAgreementSettingsMap: rASettingsMap
    })
  }

  const [updateRentalAgreementSettingsMutation, { loading: rentalAgreementLoading }] = useMutation(UPDATE_RENTAL_AGREEMENT_SETTINGS, {
    fetchPolicy: "no-cache",
    onCompleted: (data: any) => {
      snackbar({
        message: "Rental Agreement Settings updated",
        variant: SnackBarVariant.SUCCESS
      });
      dispatch(updateBranch(data.updateRentalAgreementSettings));
      if (data.updateRentalAgreementSettings.id === userState.currentBranch.id) {
        dispatch(updateUserCurrentBranch(data.updateRentalAgreementSettings));
      }
    },
    onError: (error: ApolloError) =>
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      })
  });

  const handleSubmit = () => {
    const updateRentalAgreementSettingsMap = Boolean(selectedBranch.rentalAgreementSettingsMap && Object.keys(selectedBranch.rentalAgreementSettingsMap).length);
    updateRentalAgreementSettingsMutation({
      variables: {
        organisationId: selectedOrganisationId,
        branchId: selectedBranchId,
        rentalAgreementSettings: selectedBranch.rentalAgreementSettings,
        ...(updateRentalAgreementSettingsMap && {
          rentalAgreementSettingsMap: selectedBranch.rentalAgreementSettingsMap
        })
      }
    });
  }

  if (organisationLoading) {
    return <CircularProgress />
  }

  return (
    <Grid container>
      <Grid container xs={12} spacing={2}>
        <Grid item xs={3}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="organisation-label">{getLocalizedOrganisationSyntex(country)}</InputLabel>
            <Select
              labelId="organisation-label"
              id="organisation-select-outlined"
              fullWidth
              value={selectedOrganisationId}
              onChange={(e: any) => {
                setSelectedOrganisationId(e.target.value)
                const org = organisations.find((item) => item.id === e.target.value);
                setSelectedOrganisation(org)
                if (org && org.branches) {
                  setBranches(org.branches);
                }
              }}
              label={getLocalizedOrganisationSyntex(country)}
            >
              {_.sortBy(organisations, (organisation) => organisation.name.toLowerCase()).map((organisation) => {
                return (
                  <MenuItem value={organisation.id}>
                    {organisation.name}
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        </Grid>
        {selectedOrganisationId && <Grid item xs={3}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="branch-label">Branch</InputLabel>
            <Select
              labelId="branch-label"
              id="branch-select-outlined"
              fullWidth
              value={selectedBranchId}
              onChange={(e: any) => {
                setSelectedBranchId(e.target.value)
                let item = branches.find((branch) => branch.id === e.target.value);
                if (item && item.rentalAgreementSettingsMap) {
                  item = {
                    ...item,
                    rentalAgreementSettingsMap: {
                      ...item.rentalAgreementSettingsMap,
                      [props.customerType]: replaceNullsWithKeys(item.rentalAgreementSettingsMap[props.customerType])
                    }
                  }
                  setSelectedBranch(item)
                }
              }}
              label="Branch"
            >
              {userState.role === UserRoles.BRANCH_MANAGER && userState.currentBranch.id ? (
                <MenuItem value={userState.currentBranch.id}>
                  {userState.currentBranch.name}
                </MenuItem>
              ) : (
                _.sortBy(branches, (branch) => branch.name.toLowerCase()).map((branch) => (
                  <MenuItem value={branch.id} key={branch.id}>
                    {branch.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>}
      </Grid>
      {
        selectedOrganisationId && selectedBranchId && <Grid container xs={12}>
          <NuvvenDivider />
          <RowItemsSelector
            itemName="Customer Details"
            availableList={CUSTOMER_INFO_LIST}
            selectedList={props.customerType === CustomerType.INDIVIDUAL ?
              selectedBranch?.rentalAgreementSettings?.individual.customerDetails || [] :
              selectedBranch?.rentalAgreementSettings?.business.customerDetails || []}
            customFields={props.customerType === CustomerType.INDIVIDUAL ?
              selectedBranch?.rentalAgreementSettingsMap?.individual?.customerDetails || {} :
              selectedBranch?.rentalAgreementSettingsMap?.business?.customerDetails || {}}
            updateSelected={updateSelected}
          />
          <NuvvenDivider />
          <RowItemsSelector
            itemName={`${getLocalizedBookingSyntex(country)} Details`}
            availableList={BOOKING_DETAILS_LIST}
            selectedList={props.customerType === CustomerType.INDIVIDUAL ?
              selectedBranch?.rentalAgreementSettings?.individual.bookingDetails || [] :
              selectedBranch?.rentalAgreementSettings?.business.bookingDetails || []}
            customFields={props.customerType === CustomerType.INDIVIDUAL ?
              selectedBranch?.rentalAgreementSettingsMap?.individual?.bookingDetails || {} :
              selectedBranch?.rentalAgreementSettingsMap?.business?.bookingDetails || {}}
            updateSelected={updateSelected}
          />
          <NuvvenDivider />
          <RowItemsSelector
            itemName="Extension Details"
            availableList={BOOKING_EXTENSION_DETAILS_LIST}
            selectedList={props.customerType === CustomerType.INDIVIDUAL ?
              selectedBranch?.rentalAgreementSettings?.individual.bookingExtension || [] :
              selectedBranch?.rentalAgreementSettings?.business.bookingExtension || []}
            customFields={props.customerType === CustomerType.INDIVIDUAL ?
              selectedBranch?.rentalAgreementSettingsMap?.individual?.bookingExtension || {} :
              selectedBranch?.rentalAgreementSettingsMap?.business?.bookingExtension || {}}
            updateSelected={updateSelected}
          />
          <NuvvenDivider />
          <RowItemsSelector
            itemName="Driver Details"
            availableList={DRIVER_DETAILS_LIST}
            selectedList={props.customerType === CustomerType.INDIVIDUAL ?
              selectedBranch?.rentalAgreementSettings?.individual.driverDetails || [] :
              selectedBranch?.rentalAgreementSettings?.business.driverDetails || []}
            customFields={props.customerType === CustomerType.INDIVIDUAL ?
              selectedBranch?.rentalAgreementSettingsMap?.individual?.driverDetails || {} :
              selectedBranch?.rentalAgreementSettingsMap?.business?.driverDetails || {}}
            updateSelected={updateSelected}
          />
          <NuvvenDivider />
          <RowItemsSelector
            itemName="Previous Vehicle Details"
            availableList={OLD_VEHICLE_DETAILS}
            selectedList={props.customerType === CustomerType.INDIVIDUAL ?
              selectedBranch?.rentalAgreementSettings?.individual.oldVehicleDetails || [] :
              selectedBranch?.rentalAgreementSettings?.business.oldVehicleDetails || []}
            customFields={props.customerType === CustomerType.INDIVIDUAL ?
              selectedBranch?.rentalAgreementSettingsMap?.individual?.oldVehicleDetails || {} :
              selectedBranch?.rentalAgreementSettingsMap?.business?.oldVehicleDetails || {}}
            updateSelected={updateSelected}
          />
          <NuvvenDivider />
          <RowItemsSelector
            itemName="Current Vehicle Details"
            availableList={VEHICLE_DETAILS_LIST}
            selectedList={props.customerType === CustomerType.INDIVIDUAL ?
              selectedBranch?.rentalAgreementSettings?.individual.vehicleDetails || [] :
              selectedBranch?.rentalAgreementSettings?.business.vehicleDetails || []}
            customFields={props.customerType === CustomerType.INDIVIDUAL ?
              selectedBranch?.rentalAgreementSettingsMap?.individual?.vehicleDetails || {} :
              selectedBranch?.rentalAgreementSettingsMap?.business?.vehicleDetails || {}}
            updateSelected={updateSelected}
          />
          <NuvvenDivider />
          <RowItemsSelector
            itemName="Add-on Details"
            availableList={ADDON_DETAILS_LIST}
            selectedList={props.customerType === CustomerType.INDIVIDUAL ?
              selectedBranch?.rentalAgreementSettings?.individual.addonDetails || [] :
              selectedBranch?.rentalAgreementSettings?.business.addonDetails || []}
            customFields={props.customerType === CustomerType.INDIVIDUAL ?
              selectedBranch?.rentalAgreementSettingsMap?.individual?.addonDetails || {} :
              selectedBranch?.rentalAgreementSettingsMap?.business?.addonDetails || {}}
            updateSelected={updateSelected}
          />
          <NuvvenDivider />
          <RowItemsSelector
            itemName="Insurance"
            availableList={INSURANCE_DETAILS_LIST}
            selectedList={props.customerType === CustomerType.INDIVIDUAL ?
              selectedBranch?.rentalAgreementSettings?.individual.insuranceDetails || [] :
              selectedBranch?.rentalAgreementSettings?.business.insuranceDetails || []}
            customFields={props.customerType === CustomerType.INDIVIDUAL ?
              selectedBranch?.rentalAgreementSettingsMap?.individual?.insuranceDetails || {} :
              selectedBranch?.rentalAgreementSettingsMap?.business?.insuranceDetails || {}}
            updateSelected={updateSelected}
          />
          <NuvvenDivider />
          <RowItemsSelector
            itemName="Protection Plan"
            availableList={PROTECTION_PLAN_DETAILS_LIST}
            selectedList={props.customerType === CustomerType.INDIVIDUAL ?
              selectedBranch?.rentalAgreementSettings?.individual.protectionPlanDetails || [] :
              selectedBranch?.rentalAgreementSettings?.business.protectionPlanDetails || []}
            customFields={props.customerType === CustomerType.INDIVIDUAL ?
              selectedBranch?.rentalAgreementSettingsMap?.individual?.protectionPlanDetails || {} :
              selectedBranch?.rentalAgreementSettingsMap?.business?.protectionPlanDetails || {}}
            updateSelected={updateSelected}
          />
          <NuvvenDivider />
          <RowItemsSelector
            itemName="Payment Information"
            availableList={PAYMENT_INFO_LIST}
            selectedList={props.customerType === CustomerType.INDIVIDUAL ?
              selectedBranch?.rentalAgreementSettings?.individual.paymentInfo || [] :
              selectedBranch?.rentalAgreementSettings?.business.paymentInfo || []}
            customFields={props.customerType === CustomerType.INDIVIDUAL ?
              selectedBranch?.rentalAgreementSettingsMap?.individual?.paymentInfo || {} :
              selectedBranch?.rentalAgreementSettingsMap?.business?.paymentInfo || {}}
            updateSelected={updateSelected}
          />
          <NuvvenDivider />
          <TextField
            multiline
            rows={5}
            fullWidth
            placeholder="Key Terms and Conditions"
            label="Key Terms and Conditions"
            name="tnc"
            inputProps={{
              maxLength: 5000,
              value: props.customerType === CustomerType.INDIVIDUAL ?
                selectedBranch?.rentalAgreementSettings?.individual.tnc || "" :
                selectedBranch?.rentalAgreementSettings?.business.tnc || "",
              onChange: (e: any) => {
                let rentalAgreementSettings = selectedBranch?.rentalAgreementSettings;
                if (rentalAgreementSettings) {
                  if (props.customerType === CustomerType.INDIVIDUAL) {
                    rentalAgreementSettings = {
                      ...rentalAgreementSettings,
                      individual: {
                        ...rentalAgreementSettings.individual,
                        tnc: e.target.value
                      }
                    }
                  } else {
                    rentalAgreementSettings = {
                      ...rentalAgreementSettings,
                      business: {
                        ...rentalAgreementSettings.business,
                        tnc: e.target.value
                      }
                    }
                  }
                  setSelectedBranch({
                    ...selectedBranch,
                    rentalAgreementSettings
                  })
                }
              }
            }}
          />
          <Grid container xs={12} justifyContent="flex-end">
            <Typography>
              (5000 characters max)
            </Typography>
          </Grid>
          <NuvvenDivider />
          <TextField
            multiline
            rows={5}
            fullWidth
            placeholder="Insurance Declaration"
            label="Insurance Declaration"
            name="insuranceDeclaration"
            inputProps={{
              maxLength: 3000,
              value: props.customerType === CustomerType.INDIVIDUAL ?
                selectedBranch?.rentalAgreementSettings?.individual.insuranceDeclaration || "" :
                selectedBranch?.rentalAgreementSettings?.business.insuranceDeclaration || "",
              onChange: (e: any) => {
                let rentalAgreementSettings = selectedBranch?.rentalAgreementSettings;
                if (rentalAgreementSettings) {
                  if (props.customerType === CustomerType.INDIVIDUAL) {
                    rentalAgreementSettings = {
                      ...rentalAgreementSettings,
                      individual: {
                        ...rentalAgreementSettings.individual,
                        insuranceDeclaration: e.target.value
                      }
                    }
                  } else {
                    rentalAgreementSettings = {
                      ...rentalAgreementSettings,
                      business: {
                        ...rentalAgreementSettings.business,
                        insuranceDeclaration: e.target.value
                      }
                    }
                  }
                  setSelectedBranch({
                    ...selectedBranch,
                    rentalAgreementSettings
                  })
                }
              }
            }}
          />
          <Grid container xs={12} justifyContent="flex-end">
            <Typography>
              (3000 characters max)
            </Typography>
          </Grid>
          <NuvvenDivider />
          <TextField
            multiline
            rows={5}
            fullWidth
            placeholder="Cancellation Policy"
            label="Cancellation Policy"
            name="cancellationPolicy"
            inputProps={{
              maxLength: 3000,
              value: props.customerType === CustomerType.INDIVIDUAL ?
                selectedBranch?.rentalAgreementSettings?.individual.cancellationPolicy || "" :
                selectedBranch?.rentalAgreementSettings?.business.cancellationPolicy || "",
              onChange: (e: any) => {
                let rentalAgreementSettings = selectedBranch?.rentalAgreementSettings;
                if (rentalAgreementSettings) {
                  if (props.customerType === CustomerType.INDIVIDUAL) {
                    rentalAgreementSettings = {
                      ...rentalAgreementSettings,
                      individual: {
                        ...rentalAgreementSettings.individual,
                        cancellationPolicy: e.target.value
                      }
                    }
                  } else {
                    rentalAgreementSettings = {
                      ...rentalAgreementSettings,
                      business: {
                        ...rentalAgreementSettings.business,
                        cancellationPolicy: e.target.value
                      }
                    }
                  }
                  setSelectedBranch({
                    ...selectedBranch,
                    rentalAgreementSettings
                  })
                }
              }
            }}
          />
          <Grid container xs={12} justifyContent="flex-end">
            <Typography>
              (3000 characters max)
            </Typography>
          </Grid>
          <NuvvenDivider />
          <Grid container xs={12} spacing={1}>
            <Grid item xs={3}>
              <Paper variant="outlined">
                <Box p={2}>
                  <Typography variant="body1">
                    <Grid item container xs={12}>
                      <Grid xs={10}>
                        <Typography variant="h4">
                          Security Deposit
                        </Typography>
                      </Grid>
                      <Grid xs={2}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={props.customerType === CustomerType.INDIVIDUAL ?
                                selectedBranch?.rentalAgreementSettings?.individual.securityDeposit || false :
                                selectedBranch?.rentalAgreementSettings?.business.securityDeposit || false}
                              onClick={() => {
                                let rentalAgreementSettings: IRentalAgreementSettings = { ...selectedBranch?.rentalAgreementSettings };
                                if (rentalAgreementSettings) {
                                  if (props.customerType === CustomerType.INDIVIDUAL) {
                                    rentalAgreementSettings.individual = {
                                      ...rentalAgreementSettings.individual,
                                      securityDeposit: !rentalAgreementSettings.individual.securityDeposit
                                    }
                                  } else {
                                    rentalAgreementSettings.business = {
                                      ...rentalAgreementSettings.business,
                                      securityDeposit: !rentalAgreementSettings.business.securityDeposit
                                    }
                                  }
                                  setSelectedBranch({
                                    ...selectedBranch,
                                    rentalAgreementSettings
                                  })
                                }
                              }}
                              value="checkedSwitch"
                            />
                          }
                          label=""
                        />
                      </Grid>
                    </Grid>
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper variant="outlined">
                <Box p={2}>
                  <Typography variant="body1">

                    <Grid item container xs={12}>
                      <Grid xs={10}>
                        <Typography variant="h4">
                          {`${getLocalizedBookingSyntex(country)} Questions`}
                        </Typography>
                      </Grid>
                      <Grid xs={2}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={props.customerType === CustomerType.INDIVIDUAL ?
                                selectedBranch?.rentalAgreementSettings?.individual.preBookingQuestions || false :
                                selectedBranch?.rentalAgreementSettings?.business.preBookingQuestions || false}
                              onClick={() => {
                                let rentalAgreementSettings: IRentalAgreementSettings = { ...selectedBranch?.rentalAgreementSettings };
                                if (rentalAgreementSettings) {
                                  if (props.customerType === CustomerType.INDIVIDUAL) {
                                    rentalAgreementSettings.individual = {
                                      ...rentalAgreementSettings.individual,
                                      preBookingQuestions: !rentalAgreementSettings.individual.preBookingQuestions
                                    }
                                  } else {
                                    rentalAgreementSettings.business = {
                                      ...rentalAgreementSettings.business,
                                      preBookingQuestions: !rentalAgreementSettings.business.preBookingQuestions
                                    }
                                  }
                                  setSelectedBranch({
                                    ...selectedBranch,
                                    rentalAgreementSettings
                                  })
                                }
                              }}
                              value="checkedSwitch"
                            />
                          }
                          label=""
                        />
                      </Grid>

                    </Grid>
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={3}>
              <Paper variant="outlined">
                <Box p={2}>
                  <Typography variant="body1">

                    <Grid item container xs={12}>
                      <Grid xs={10}>
                        <Typography variant="h4">
                          {'Additional Driver Questions And Signature'}
                        </Typography>
                      </Grid>
                      <Grid xs={2}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={props.customerType === CustomerType.INDIVIDUAL ?
                                selectedBranch?.rentalAgreementSettings?.individual.additionalDriverQuestions || false :
                                selectedBranch?.rentalAgreementSettings?.business.additionalDriverQuestions || false}
                              onClick={() => {
                                let rentalAgreementSettings: IRentalAgreementSettings = { ...selectedBranch?.rentalAgreementSettings };
                                if (rentalAgreementSettings) {
                                  let updatedRentalAgreementSettings = {
                                    ...rentalAgreementSettings,
                                    individual: { ...rentalAgreementSettings.individual },
                                    business: { ...rentalAgreementSettings.business }
                                  };
                                  if (props.customerType === CustomerType.INDIVIDUAL) {
                                    updatedRentalAgreementSettings.individual.additionalDriverQuestions = !rentalAgreementSettings.individual.additionalDriverQuestions;
                                  } else {
                                    updatedRentalAgreementSettings.business.additionalDriverQuestions = !rentalAgreementSettings.business.additionalDriverQuestions;
                                  }
                                  setSelectedBranch({
                                    ...selectedBranch,
                                    rentalAgreementSettings: updatedRentalAgreementSettings
                                  });
                                }
                              }}
                              value="checkedSwitch"
                            />
                          }
                          label=""
                        />
                      </Grid>

                    </Grid>
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={3}>
              <Paper variant="outlined">
                <Box p={2}>
                  <Typography variant="body1">
                    <Grid item container xs={12}>
                      <Grid xs={10}>
                        <Typography variant="h4">
                          Vehicle Handover Questions
                        </Typography>
                      </Grid>
                      <Grid xs={2}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={props.customerType === CustomerType.INDIVIDUAL ?
                                selectedBranch?.rentalAgreementSettings?.individual.deliveryQuestions || false :
                                selectedBranch?.rentalAgreementSettings?.business.deliveryQuestions || false}
                              onClick={() => {
                                let rentalAgreementSettings: IRentalAgreementSettings = { ...selectedBranch?.rentalAgreementSettings };
                                if (rentalAgreementSettings) {
                                  if (props.customerType === CustomerType.INDIVIDUAL) {
                                    rentalAgreementSettings.individual = {
                                      ...rentalAgreementSettings.individual,
                                      deliveryQuestions: !rentalAgreementSettings.individual.deliveryQuestions
                                    }
                                  } else {
                                    rentalAgreementSettings.business = {
                                      ...rentalAgreementSettings.business,
                                      deliveryQuestions: !rentalAgreementSettings.business.deliveryQuestions
                                    }
                                  }
                                  setSelectedBranch({
                                    ...selectedBranch,
                                    rentalAgreementSettings
                                  })
                                }
                              }}
                              value="checkedSwitch"
                            />
                          }
                          label=""
                        />
                      </Grid>

                    </Grid>
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper variant="outlined">
                <Box p={2}>
                  <Typography variant="body1">
                    <Grid item container xs={12}>
                      <Grid xs={10}>
                        <Typography variant="h4">
                          Additional Customer Information
                        </Typography>
                      </Grid>
                      <Grid xs={2}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={props.customerType === CustomerType.INDIVIDUAL ?
                                selectedBranch?.rentalAgreementSettings?.individual.additionalInfo || false :
                                selectedBranch?.rentalAgreementSettings?.business.additionalInfo || false}
                              onClick={() => {
                                let rentalAgreementSettings: IRentalAgreementSettings = { ...selectedBranch?.rentalAgreementSettings };
                                if (rentalAgreementSettings) {
                                  if (props.customerType === CustomerType.INDIVIDUAL) {
                                    rentalAgreementSettings.individual = {
                                      ...rentalAgreementSettings.individual,
                                      additionalInfo: !rentalAgreementSettings.individual.additionalInfo
                                    }
                                  } else {
                                    rentalAgreementSettings.business = {
                                      ...rentalAgreementSettings.business,
                                      additionalInfo: !rentalAgreementSettings.business.additionalInfo
                                    }
                                  }
                                  setSelectedBranch({
                                    ...selectedBranch,
                                    rentalAgreementSettings
                                  })
                                }
                              }}
                              value="checkedSwitch"
                            />
                          }
                          label=""
                        />
                      </Grid>
                    </Grid>
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper variant="outlined">
                <Box p={2}>
                  <Typography variant="body1">
                    <Grid item container xs={12}>
                      <Grid xs={10}>
                        <Typography variant="h4">
                          {"Additional T&C"}
                        </Typography>
                      </Grid>
                      <Grid xs={2}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={props.customerType === CustomerType.INDIVIDUAL ?
                                selectedBranch?.rentalAgreementSettings?.individual.additonalTnc || false :
                                selectedBranch?.rentalAgreementSettings?.business.additonalTnc || false}
                              onClick={() => {
                                let rentalAgreementSettings: IRentalAgreementSettings = { ...selectedBranch?.rentalAgreementSettings };
                                if (rentalAgreementSettings) {
                                  if (props.customerType === CustomerType.INDIVIDUAL) {
                                    rentalAgreementSettings.individual = {
                                      ...rentalAgreementSettings.individual,
                                      additonalTnc: !rentalAgreementSettings.individual.additonalTnc
                                    }
                                  } else {
                                    rentalAgreementSettings.business = {
                                      ...rentalAgreementSettings.business,
                                      additonalTnc: !rentalAgreementSettings.business.additonalTnc
                                    }
                                  }
                                  setSelectedBranch({
                                    ...selectedBranch,
                                    rentalAgreementSettings
                                  })
                                }
                              }}
                              value="checkedSwitch"
                            />
                          }
                          label=""
                        />
                      </Grid>
                    </Grid>
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
          <NuvvenDivider />
          <Grid container item xs={12} >
            <Fab
              variant="extended"
              size="medium"
              onClick={() => setOpenPreview(true)}
            >
              Preview
            </Fab>
            <Box ml={2}></Box>
            <Fab
              className="blackBackButton"
              variant="extended"
              size="medium"
              onClick={() => {
                setSelectedBranchId("");
                setSelectedOrganisationId("");
                getOrganisations();
              }}
            >
              CANCEL
            </Fab>
            <Box ml={2}></Box>
            <Fab
              variant="extended"
              size="medium"
              onClick={handleSubmit}
              disabled={rentalAgreementLoading}
            >
              {rentalAgreementLoading && (
                <CircularProgress
                  size={14}
                  style={{ color: "white", marginRight: "10px" }}
                />
              )}
              SAVE
            </Fab>
          </Grid>
          {
            openPreview && (
              <RAPreviewFormatIndividualCustomer
                open={openPreview}
                onClose={() => setOpenPreview(false)}
                customerType={props.customerType}
                selectedBranch={props.customerType === CustomerType.INDIVIDUAL ?
                  selectedBranch?.rentalAgreementSettings?.individual :
                  selectedBranch?.rentalAgreementSettings?.business
                }
                organisation={selectedOrgainisation}
                customSettings={selectedBranch.rentalAgreementSettingsMap ? selectedBranch.rentalAgreementSettingsMap[props.customerType] : {}}
              />
            )
          }
        </Grid>
      }
    </Grid>
  )
};

export default CustomerBasedSettings;